import React, { ReactNode } from 'react';

import {
  differenceInSeconds,
  differenceInMonths,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  subSeconds,
} from 'date-fns';
import { FormattedHTMLMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import Tooltip from '@atlaskit/tooltip';

import { useIntl } from 'src/hooks/intl';
import { getCurrentUserUuid } from 'src/selectors/user-selectors';
import { PullRequest } from 'src/types/pull-requests-table';

import { TooltipBoundaries } from '../pull-request-row.style';

import { PrCycleMetrics } from './api/requests';
import { fireCycleTimeAnalytics } from './cycle-time-analytics';
import { messages } from './cycle-time.i18n';

export const useConvertSecondsToReadableTime = (
  secondsInPast: number,
  now: Date = new Date()
): string => {
  const intl = useIntl();

  const past = subSeconds(now, secondsInPast);

  const months = differenceInMonths(now, past);
  const days = differenceInDays(now, past);
  const hours = differenceInHours(now, past);
  const minutes = differenceInMinutes(now, past);

  if (months >= 1) {
    return intl.formatMessage(messages.month, { months });
  } else if (days >= 1) {
    return intl.formatMessage(messages.day, { days });
  } else if (hours >= 1) {
    return intl.formatMessage(messages.hour, { hours });
  } else if (minutes >= 1) {
    return intl.formatMessage(messages.minute, { minutes });
  } else {
    return intl.formatMessage(messages.second, { seconds: secondsInPast });
  }
};

export type CycleTimeTooltipProps = {
  children: ReactNode;
  pullRequest: PullRequest;
  cycleTimeMetrics?: PrCycleMetrics;
};
export const CycleTimeTooltip = (props: CycleTimeTooltipProps) => {
  const { children, pullRequest, cycleTimeMetrics } = props;

  const currentUserUuid = useSelector(getCurrentUserUuid);
  const usuallyMergedIn = cycleTimeMetrics?.high?.value;

  const created = useConvertSecondsToReadableTime(
    differenceInSeconds(new Date(), new Date(pullRequest.created_on))
  );

  const usual = useConvertSecondsToReadableTime(usuallyMergedIn ?? 0);

  if (!usuallyMergedIn) {
    return <>{children}</>;
  }

  const content = (
    <FormattedHTMLMessage
      tagName="p"
      {...messages.prOpenVsUsual}
      values={{
        created,
        usual: usuallyMergedIn ? usual : undefined,
      }}
    />
  );

  const onShow = () => {
    fireCycleTimeAnalytics({
      action: 'hovered',
      metrics: cycleTimeMetrics,
      pullRequest,
      currentUserUuid,
      stale: true,
    });
  };

  return (
    <Tooltip content={content} onShow={onShow}>
      <TooltipBoundaries>{children}</TooltipBoundaries>
    </Tooltip>
  );
};
